import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Card, CardContent, Typography, Button, TextField, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TablePagination, Dialog, DialogTitle, DialogContent,
  DialogActions, Grid, IconButton, Chip, InputAdornment, MenuItem, Switch,
  FormControlLabel, Accordion, AccordionSummary, AccordionDetails, Autocomplete,
  Tooltip, alpha, Avatar, Divider, Checkbox, Skeleton
} from '@mui/material';
import {
  Add, Edit, Delete, Search, Business, ExpandMore, Close,
  Phone, Email, CalendarToday, Notes, LocationOn,
  PersonAdd, CheckBox, CheckBoxOutlineBlank,
  VisibilityOff, Visibility, OpenInNew
} from '@mui/icons-material';
import { providersAPI, providerServicesAPI, contractorsAPI } from '../../services/api';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { AccountBalance } from '@mui/icons-material';
import { formatPhone, fullName, US_STATES } from '../../utils/formatters';
import { useTableSort } from '../../components/useTableSort';

const empty = {
  firstName: '', lastName: '', middleInitial: '', phone: '', groupName: '', email: '',
  street: '', city: '', state: '', zip: '', country: 'US',
  billingRate: '', invoiceSchedule: 'monthly', invoiceDueDate: '1st_15th', startDate: '', endDate: '',
  isActive: true, notes: '', services: [], contractors: []
};

const Providers = () => {
  const [rows, setRows] = useState([]);
  const [allServices, setAllServices] = useState([]);
  const [allContractors, setAllContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rpp, setRpp] = useState(10);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [detailPopup, setDetailPopup] = useState(null);

  const load = useCallback(async () => {
    try {
      const [p, s, c] = await Promise.all([
        providersAPI.getAll({ limit: 1000 }), providerServicesAPI.getAll({ limit: 1000 }), contractorsAPI.getAll({ limit: 1000 })
      ]);
      setRows(Array.isArray(p.data) ? p.data : p.data?.data || []);
      setAllServices(Array.isArray(s.data) ? s.data : s.data?.data || []);
      setAllContractors(Array.isArray(c.data) ? c.data : c.data?.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { document.title = 'Providers | MyPayPlus'; }, []);
  useEffect(() => { load(); }, [load]);

  const visibleRows = showInactive ? rows : rows.filter(r => r.isActive !== false);
  const inactiveCount = rows.filter(r => r.isActive === false).length;

  const columns = useMemo(() => [
    { id: 'providerId', label: 'ID', getValue: r => r.providerId || '' },
    { id: 'provider', label: 'Provider', getValue: r => fullName(r) || '' },
    { id: 'groupName', label: 'Group', getValue: r => r.groupName || '' },
    { id: 'contact', label: 'Contact', getValue: r => r.phone || '' },
    { id: 'contractors', label: 'Contractors', getValue: r => (r.assignedContractors || []).filter(a => a.contractor).length },
    { id: 'billingRate', label: 'Rate', getValue: r => parseFloat(r.billingRate) || 0, getDisplay: r => r.billingRate ? `${r.billingRate}%` : '—' },
    { id: 'invoiceSchedule', label: 'Schedule', getValue: r => r.invoiceSchedule || 'monthly', getDisplay: r => r.invoiceSchedule || 'Monthly' },
    { id: 'invoiceDueDate', label: 'Due Date', getValue: r => r.invoiceDueDate || '1st_15th', getDisplay: r => r.invoiceDueDate === '15th_1st' ? 'Inv 15th, Pay 1st' : 'Inv 1st, Pay 15th' },
    { id: 'status', label: 'Status', getValue: r => r.isActive !== false ? 'Active' : 'Inactive' },
  ], []);

  const searchFiltered = useMemo(() => {
    if (!search) return visibleRows;
    return visibleRows.filter(r =>
      [r.firstName, r.lastName, r.groupName, r.email, r.phone, r.providerId]
        .some(v => (v || '').toLowerCase().includes(search.toLowerCase()))
    );
  }, [visibleRows, search]);

  const { sortedData: filtered, renderHeader, renderFilterPopover, renderClearFilters } = useTableSort(columns, searchFiltered);

  const handleOpen = (row) => {
    if (row) {
      setEditing(row._id);
      setForm({
        firstName: row.firstName || '', lastName: row.lastName || '', middleInitial: row.middleInitial || '',
        phone: row.phone || '', groupName: row.groupName || '',
        email: row.email || '',
        street: row.street || '', city: row.city || '', state: row.state || '', zip: row.zip || '', country: row.country || 'US',
        billingRate: row.billingRate || '', invoiceSchedule: row.invoiceSchedule || 'monthly', invoiceDueDate: row.invoiceDueDate || '1st_15th',
        startDate: row.startDate ? row.startDate.slice(0, 10) : '',
        endDate: row.endDate ? row.endDate.slice(0, 10) : '',
        isActive: row.isActive !== false, notes: row.notes || '',
        services: (row.services || []).map(s => s.service?._id || s.service || s),
        contractors: (row.assignedContractors || []).map(c => c.contractor?._id || c.contractor || c)
      });
    } else {
      setEditing(null);
      setForm(empty);
    }
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      const payload = {
        ...form,
        services: form.services.map(sId => ({ service: sId })),
        assignedContractors: form.contractors.map(cId => ({ contractor: cId, payRate: 0, isActive: true })),
      };
      delete payload.contractors;
      if (editing) await providersAPI.update(editing, payload);
      else await providersAPI.create(payload);
      setOpen(false); load();
      toast.success(editing ? 'Provider updated successfully' : 'Provider created successfully');
    } catch (e) { toast.error(e.response?.data?.message || 'Save failed'); }
  };

  const handleDelete = async () => {
    try { await providersAPI.delete(deleteId); setDeleteId(null); load(); toast.success('Provider deleted'); }
    catch (e) { toast.error('Delete failed'); }
  };

  const F = (field, val) => setForm(p => ({ ...p, [field]: val }));

  const navigate = useNavigate();

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4">Providers</Typography>
          <Typography variant="body2" color="text.secondary">Manage healthcare providers and their services</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button variant="outlined" startIcon={<AccountBalance />} onClick={() => navigate('/collections')}
            sx={{ borderColor: '#f59e0b', color: '#d97706', fontWeight: 700, '&:hover': { borderColor: '#d97706', bgcolor: 'rgba(245,158,11,0.06)' } }}>
            Add Collection
          </Button>
          <Button variant="contained" startIcon={<Add />} onClick={() => handleOpen(null)}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              boxShadow: '0 4px 14px rgba(59,130,246,0.3)',
              px: 3, fontWeight: 700
            }}>
            Add Provider
          </Button>
        </Box>
      </Box>

      {/* Table Card */}
      <Card>
        <CardContent sx={{ p: 0 }}>
          {/* Search */}
          <Box sx={{ px: 3, pt: 2.5, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              size="small" placeholder="Search providers..." value={search}
              onChange={e => { setSearch(e.target.value); setPage(0); }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment>
              }}
              sx={{ width: 320 }}
            />
            <Tooltip title={showInactive ? 'Hide inactive providers' : `Show inactive providers (${inactiveCount})`}>
              <IconButton onClick={() => setShowInactive(p => !p)} sx={{ color: showInactive ? '#ef4444' : '#94a3b8' }}>
                {showInactive ? <Visibility /> : <VisibilityOff />}
              </IconButton>
            </Tooltip>
            {renderClearFilters()}
          </Box>

          {loading ? (
            <Box sx={{ p: 3 }}>{[...Array(5)].map((_, i) => <Skeleton key={i} height={52} sx={{ mb: 0.5 }} />)}</Box>
          ) : (
            <>
              <TableContainer>
                <Table>
                  {renderHeader(<TableCell align="right">Actions</TableCell>)}
                  <TableBody>
                    {filtered.length === 0 ? (
                      <TableRow>
                            <TableCell colSpan={9} sx={{ py: 6, textAlign: 'center' }}>
                          <Business sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                          <Typography color="text.secondary">No providers found</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      filtered.slice(page * rpp, page * rpp + rpp).map(r => (
                        <TableRow key={r._id} hover sx={{ cursor: 'pointer', '&:hover': { bgcolor: alpha('#3b82f6', 0.03) } }}>
                          <TableCell>
                            <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#3b82f6', fontFamily: 'monospace' }}>
                              {r.providerId}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{
                                width: 36, height: 36, fontSize: '0.8rem', fontWeight: 700,
                                background: `linear-gradient(135deg, ${alpha('#3b82f6', 0.15)}, ${alpha('#8b5cf6', 0.15)})`,
                                color: '#3b82f6'
                              }}>
                                {(r.firstName || '?')[0]}
                              </Avatar>
                              <Typography
                                onClick={() => setDetailPopup(r)}
                                sx={{ fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', '&:hover': { color: '#3b82f6', textDecoration: 'underline' } }}>
                                {fullName(r)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell><Typography sx={{ fontSize: '0.82rem', color: 'text.secondary' }}>{r.groupName || '—'}</Typography></TableCell>
                          <TableCell>
                            <Typography sx={{ fontSize: '0.78rem' }}>{r.phone || '—'}</Typography>
                            <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{r.email || ''}</Typography>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const assigned = (r.assignedContractors || []).filter(a => a.contractor);
                              const show = assigned.slice(0, 2);
                              const extra = assigned.length - 2;
                              return assigned.length ? (
                                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                                  {show.map((a, i) => <Chip key={i} label={fullName(a.contractor)} size="small" sx={{ fontWeight: 600, height: 22, fontSize: '0.7rem', bgcolor: alpha('#8b5cf6', 0.08), color: '#7c3aed' }} />)}
                                  {extra > 0 && <Tooltip title={assigned.slice(2).map(a => fullName(a.contractor)).join(', ')}><Chip label={`+${extra}`} size="small" sx={{ fontWeight: 700, height: 22, fontSize: '0.7rem', bgcolor: alpha('#64748b', 0.1), color: '#475569', cursor: 'pointer' }} /></Tooltip>}
                                </Box>
                              ) : <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary' }}>—</Typography>;
                            })()}
                          </TableCell>
                          <TableCell><Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{r.billingRate ? `${r.billingRate}%` : '—'}</Typography></TableCell>
                          <TableCell><Chip label={r.invoiceSchedule || 'Monthly'} size="small" sx={{ fontWeight: 600, height: 24, bgcolor: alpha('#3b82f6', 0.08), color: '#3b82f6' }} /></TableCell>
                          <TableCell><Chip label={r.invoiceDueDate === '15th_1st' ? 'Inv 15th, Pay 1st' : 'Inv 1st, Pay 15th'} size="small" sx={{ fontWeight: 600, height: 24, bgcolor: alpha('#f59e0b', 0.08), color: '#d97706' }} /></TableCell>
                          <TableCell>
                            <Chip label={r.isActive !== false ? 'Active' : 'Inactive'} size="small"
                              sx={{
                                fontWeight: 700, height: 24,
                                bgcolor: r.isActive !== false ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1),
                                color: r.isActive !== false ? '#059669' : '#dc2626'
                              }} />
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit"><IconButton size="small" onClick={() => handleOpen(r)} sx={{ color: '#3b82f6' }}><Edit sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                            <Tooltip title="Delete"><IconButton size="small" onClick={() => setDeleteId(r._id)} sx={{ color: '#ef4444' }}><Delete sx={{ fontSize: 18 }} /></IconButton></Tooltip>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div" count={filtered.length} page={page} rowsPerPage={rpp}
                onPageChange={(_, p) => setPage(p)} onRowsPerPageChange={e => { setRpp(+e.target.value); setPage(0); }}
                rowsPerPageOptions={[5, 10, 25, 50]}
              />
            </>
          )}
        </CardContent>
      </Card>
      {renderFilterPopover()}

      {/* Create/Edit Dialog */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 4, maxHeight: '90vh' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ width: 36, height: 36, background: 'linear-gradient(135deg, #3b82f6, #8b5cf6)', borderRadius: 2 }}>
              <Business sx={{ fontSize: 20 }} />
            </Avatar>
            <Box>
              <Typography sx={{ fontWeight: 700, fontSize: '1.05rem' }}>{editing ? 'Edit Provider' : 'Add Provider'}</Typography>
              <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>Fill in the provider details</Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setOpen(false)} size="small"><Close /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>First Name *</Typography>
              <TextField fullWidth size="small" placeholder="John" value={form.firstName} onChange={e => F('firstName', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Business sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={5}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Last Name *</Typography>
              <TextField fullWidth size="small" placeholder="Smith" value={form.lastName} onChange={e => F('lastName', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={2}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>MI</Typography>
              <TextField fullWidth size="small" placeholder="A" inputProps={{ maxLength: 1 }} value={form.middleInitial} onChange={e => F('middleInitial', e.target.value.toUpperCase())} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Group Name</Typography>
              <TextField fullWidth size="small" placeholder="Clinic name" value={form.groupName} onChange={e => F('groupName', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Phone</Typography>
              <TextField fullWidth size="small" placeholder="+1 (555) 000-0000" value={form.phone} onChange={e => F('phone', formatPhone(e.target.value))}
                InputProps={{ startAdornment: <InputAdornment position="start"><Phone sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Email</Typography>
              <TextField fullWidth size="small" placeholder="email@example.com" value={form.email} onChange={e => F('email', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Street Address</Typography>
              <TextField fullWidth size="small" placeholder="123 Main St" value={form.street} onChange={e => F('street', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><LocationOn sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>City</Typography>
              <TextField fullWidth size="small" placeholder="New York" value={form.city} onChange={e => F('city', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>State</Typography>
              <TextField fullWidth size="small" select value={form.state} onChange={e => F('state', e.target.value)}>
                <MenuItem value="">—</MenuItem>
                {US_STATES.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={2}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Zip</Typography>
              <TextField fullWidth size="small" placeholder="10001" value={form.zip} onChange={e => F('zip', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={3}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Country</Typography>
              <TextField fullWidth size="small" value={form.country} onChange={e => F('country', e.target.value)} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Billing Rate (%)</Typography>
              <TextField fullWidth size="small" type="number" placeholder="10" value={form.billingRate} onChange={e => F('billingRate', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Typography sx={{ fontSize: '0.9rem', fontWeight: 700, color: '#94a3b8' }}>%</Typography></InputAdornment> }} />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Invoice Schedule</Typography>
              <TextField fullWidth size="small" select value={form.invoiceSchedule} onChange={e => F('invoiceSchedule', e.target.value)}>
                {[{ v: 'weekly', l: 'Weekly' }, { v: 'bi-weekly', l: 'Bi-Weekly' }, { v: 'monthly', l: 'Monthly' }].map(o => <MenuItem key={o.v} value={o.v}>{o.l}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Invoice Due Date</Typography>
              <TextField fullWidth size="small" select value={form.invoiceDueDate} onChange={e => F('invoiceDueDate', e.target.value)}>
                <MenuItem value="1st_15th">1st / 15th — Invoice 1st, Pay 15th</MenuItem>
                <MenuItem value="15th_1st">15th / 1st — Invoice 15th, Pay 1st</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12} sm={4}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Status</Typography>
              <FormControlLabel control={<Switch checked={form.isActive} onChange={e => F('isActive', e.target.checked)}
                sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#10b981' }, '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { bgcolor: '#10b981' } }} />}
                label={<Typography sx={{ fontSize: '0.82rem', fontWeight: 600 }}>{form.isActive ? 'Active' : 'Inactive'}</Typography>} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Start Date</Typography>
              <TextField fullWidth size="small" type="date" value={form.startDate} onChange={e => F('startDate', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><CalendarToday sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>End Date</Typography>
              <TextField fullWidth size="small" type="date" value={form.endDate} onChange={e => F('endDate', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><CalendarToday sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#94a3b8', mb: 0.8 }}>Notes</Typography>
              <TextField fullWidth size="small" multiline rows={2} placeholder="Additional notes..." value={form.notes} onChange={e => F('notes', e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Notes sx={{ fontSize: 18, color: '#94a3b8' }} /></InputAdornment> }} />
            </Grid>
          </Grid>

          {/* Services */}
          <Accordion sx={{ mt: 3, borderRadius: '12px !important', '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Services</Typography>
              <Chip size="small" label={form.services.length} sx={{ ml: 1.5, height: 22, fontWeight: 700, bgcolor: alpha('#3b82f6', 0.1), color: '#3b82f6' }} />
            </AccordionSummary>
            <AccordionDetails>
              <Autocomplete multiple disableCloseOnSelect
                options={allServices} getOptionLabel={o => o.name || ''}
                value={allServices.filter(s => form.services.includes(s._id))}
                onChange={(_, v) => F('services', v.map(s => s._id))}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={<CheckBoxOutlineBlank fontSize="small" />} checkedIcon={<CheckBox fontSize="small" />} checked={selected} sx={{ mr: 1 }} />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => <TextField {...params} size="small" placeholder="Select services..." />}
              />
            </AccordionDetails>
          </Accordion>

          {/* Contractors */}
          <Accordion sx={{ mt: 2, borderRadius: '12px !important', '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider' }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>Assigned Contractors</Typography>
              <Chip size="small" label={form.contractors.length} sx={{ ml: 1.5, height: 22, fontWeight: 700, bgcolor: alpha('#8b5cf6', 0.1), color: '#8b5cf6' }} />
            </AccordionSummary>
            <AccordionDetails>
              <Autocomplete multiple disableCloseOnSelect
                options={allContractors} getOptionLabel={o => fullName(o) || ''}
                value={allContractors.filter(c => form.contractors.includes(c._id))}
                onChange={(_, v) => F('contractors', v.map(c => c._id))}
                renderOption={(props, option, { selected }) => (
                  <li {...props}>
                    <Checkbox icon={<CheckBoxOutlineBlank fontSize="small" />} checkedIcon={<CheckBox fontSize="small" />} checked={selected} sx={{ mr: 1 }} />
                    <Box>
                      <Typography sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{fullName(option)}</Typography>
                      <Typography sx={{ fontSize: '0.72rem', color: 'text.secondary' }}>{option.phone || ''}</Typography>
                    </Box>
                  </li>
                )}
                renderInput={(params) => <TextField {...params} size="small" placeholder="Assign contractors..." />}
              />
            </AccordionDetails>
          </Accordion>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setOpen(false)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}
            sx={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700, px: 4 }}>
            {editing ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm */}
      <Dialog open={!!deleteId} onClose={() => setDeleteId(null)} PaperProps={{ sx: { borderRadius: 3, p: 1 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Provider?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>This action cannot be undone. All associated data will be removed.</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)} sx={{ color: '#64748b', fontWeight: 600 }}>Cancel</Button>
          <Button variant="contained" onClick={handleDelete} sx={{ bgcolor: '#ef4444', fontWeight: 700, '&:hover': { bgcolor: '#dc2626' } }}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Provider Detail Popup */}
      <Dialog open={!!detailPopup} onClose={() => setDetailPopup(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        {detailPopup && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Avatar sx={{ width: 40, height: 40, background: 'linear-gradient(135deg, #3b82f6, #2563eb)', fontWeight: 700 }}>
                  {(detailPopup.firstName || '?')[0]}
                </Avatar>
                <Box>
                  <Typography sx={{ fontWeight: 700 }}>{fullName(detailPopup)}</Typography>
                  <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', fontFamily: 'monospace' }}>{detailPopup.providerId}</Typography>
                </Box>
              </Box>
              <Box>
                <Tooltip title="Edit Provider">
                  <IconButton size="small" onClick={() => { setDetailPopup(null); handleOpen(detailPopup); }} sx={{ color: '#3b82f6' }}>
                    <OpenInNew sx={{ fontSize: 18 }} />
                  </IconButton>
                </Tooltip>
                <IconButton size="small" onClick={() => setDetailPopup(null)}><Close /></IconButton>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 2 }}>
              <Grid container spacing={1.5}>
                {detailPopup.email && <Grid item xs={12}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Email</Typography><Typography sx={{ fontSize: '0.85rem' }}>{detailPopup.email}</Typography></Grid>}
                {detailPopup.phone && <Grid item xs={6}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Phone</Typography><Typography sx={{ fontSize: '0.85rem' }}>{formatPhone(detailPopup.phone)}</Typography></Grid>}
                {detailPopup.groupName && <Grid item xs={6}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Group</Typography><Typography sx={{ fontSize: '0.85rem' }}>{detailPopup.groupName}</Typography></Grid>}
                <Grid item xs={6}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Status</Typography><Chip label={detailPopup.isActive !== false ? 'Active' : 'Inactive'} size="small" sx={{ fontWeight: 700, height: 22, bgcolor: detailPopup.isActive !== false ? alpha('#10b981', 0.1) : alpha('#ef4444', 0.1), color: detailPopup.isActive !== false ? '#059669' : '#dc2626' }} /></Grid>
                {detailPopup.billingRate && <Grid item xs={6}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Billing Rate</Typography><Typography sx={{ fontSize: '0.85rem' }}>{detailPopup.billingRate}%</Typography></Grid>}
                {(detailPopup.street || detailPopup.city) && <Grid item xs={12}><Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Address</Typography><Typography sx={{ fontSize: '0.85rem' }}>{detailPopup.street || ''}</Typography>{(detailPopup.city || detailPopup.state || detailPopup.zip) && <Typography sx={{ fontSize: '0.85rem' }}>{[detailPopup.city, detailPopup.state].filter(Boolean).join(', ')}{detailPopup.zip ? ` ${detailPopup.zip}` : ''}</Typography>}{detailPopup.country && detailPopup.country !== 'US' && <Typography sx={{ fontSize: '0.85rem' }}>{detailPopup.country}</Typography>}</Grid>}
              </Grid>
            </DialogContent>
          </>
        )}
      </Dialog>
    </Box>
  );
};

export default Providers;
